import React, { forwardRef, useMemo } from "react";
import ReactHtmlParser from "react-html-parser";
import { buildShareLabels } from "./ShareSectionDrawer";
import hhLogo from "./hh-logo.png";
import "./LessonNotes.css";
import { imageUrlFromNode } from "./noteUtils";

function isMediaClass(className = "") {
  return className === "YouTubeVideo" || className === "LottieAnimation";
}

function isImageClass(className = "") {
  return String(className).toLowerCase().includes("image");
}

export function sectionHasShareableContent(item) {
  if (!item) return false;
  if (isMediaClass(item.className)) return false;
  if (imageUrlFromNode(item)) return true;
  if (
    item.content &&
    String(item.content)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  ) {
    return true;
  }
  return (
    (item.subSections || []).some(sectionHasShareableContent) ||
    (item.miniSections || []).some(sectionHasShareableContent)
  );
}

function stripHtmlToText(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const MIN_IMAGE_MAX = 120;

function imageMaxForColumns(columnCount) {
  if (columnCount <= 1) return 280;
  if (columnCount === 2) return 180;
  return 140;
}

function groupedImageHeight(count, imageMax) {
  if (count <= 1) return imageMax + 16;
  if (count === 2) return Math.round(imageMax * 0.72) + 16;
  const rows = Math.ceil(count / 3);
  return rows * (Math.round(imageMax * 0.5) + 12);
}

function estimateNodeHeight(item, columnCount, imageMax) {
  if (!item || isMediaClass(item.className)) return 0;
  const className = item.className || "";
  if (isImageClass(className)) {
    return groupedImageHeight(1, imageMax);
  }

  let height = 0;
  if (item.content) {
    const text = stripHtmlToText(item.content);
    const charsPerLine = columnCount <= 1 ? 42 : columnCount === 2 ? 32 : 26;
    height += Math.max(1, Math.ceil(text.length / charsPerLine)) * 30;
    if (
      /arabic(Large|VeryLarge|Normal)Size/i.test(item.content) ||
      /[\u0600-\u06FF]/.test(item.content)
    ) {
      height += 50;
    }
  }
  const style = { ...(item.style || {}), ...(item.contentStyle || {}) };
  if (style.backgroundColor) height += 18;
  if (style.padding) height += 12;

  const children = [...(item.subSections || []), ...(item.miniSections || [])];
  const imageChildren = children.filter((child) => isImageClass(child.className));
  const otherChildren = children.filter(
    (child) =>
      !isImageClass(child.className) && !isMediaClass(child.className)
  );

  if (imageChildren.length > 0) {
    height += groupedImageHeight(imageChildren.length, imageMax);
  }
  otherChildren.forEach((child) => {
    height += estimateNodeHeight(child, columnCount, imageMax);
  });
  return height;
}

function estimateImageContribution(item, columnCount, imageMax) {
  if (!item || isMediaClass(item.className)) return 0;
  if (isImageClass(item.className)) {
    return groupedImageHeight(1, imageMax);
  }
  const children = [...(item.subSections || []), ...(item.miniSections || [])];
  const imageChildren = children.filter((child) => isImageClass(child.className));
  const otherChildren = children.filter(
    (child) =>
      !isImageClass(child.className) && !isMediaClass(child.className)
  );
  let height = 0;
  if (imageChildren.length > 0) {
    height += groupedImageHeight(imageChildren.length, imageMax);
  }
  otherChildren.forEach((child) => {
    height += estimateImageContribution(child, columnCount, imageMax);
  });
  return height;
}

function estimateSectionHeight(section, columnCount, imageMax) {
  return 56 + estimateNodeHeight(section, columnCount, imageMax);
}

function sumHeights(heights, start, end) {
  let total = 0;
  for (let i = start; i < end; i += 1) total += heights[i];
  return total;
}

const POSTER_WIDTH = 1080;
const MAX_COLUMNS = 3;

function partitionCuts(heights, columnCount) {
  const n = heights.length;
  if (columnCount <= 1) return [n];
  if (columnCount >= n) {
    return Array.from({ length: n }, (_, index) => index + 1);
  }

  const prefix = [0];
  heights.forEach((height) => prefix.push(prefix[prefix.length - 1] + height));
  const range = (start, end) => prefix[end] - prefix[start];

  const dp = Array.from({ length: n + 1 }, () =>
    Array.from({ length: columnCount + 1 }, () => null)
  );
  dp[0][0] = { cost: 0, prev: 0 };

  for (let cols = 1; cols <= columnCount; cols += 1) {
    for (let i = cols; i <= n; i += 1) {
      for (let j = cols - 1; j < i; j += 1) {
        if (!dp[j][cols - 1]) continue;
        const cost = Math.max(dp[j][cols - 1].cost, range(j, i));
        if (!dp[i][cols] || cost < dp[i][cols].cost) {
          dp[i][cols] = { cost, prev: j };
        }
      }
    }
  }

  const cuts = [];
  let index = n;
  let cols = columnCount;
  while (cols > 0) {
    cuts.push(index);
    index = dp[index][cols].prev;
    cols -= 1;
  }
  cuts.reverse();
  return cuts;
}

function maxColumnHeight(heights, cuts) {
  let start = 0;
  let maxHeight = 0;
  cuts.forEach((end) => {
    maxHeight = Math.max(maxHeight, sumHeights(heights, start, end));
    start = end;
  });
  return maxHeight;
}

function columnHeights(heights, cuts) {
  const result = [];
  let start = 0;
  cuts.forEach((end) => {
    result.push(sumHeights(heights, start, end));
    start = end;
  });
  return result;
}

function scoreColumnCount(columnCount, sectionCount, maxHeight, heightDiff) {
  const aspect = maxHeight / POSTER_WIDTH;
  let score = 0;
  if (aspect < 0.55) score += (0.55 - aspect) * 1.1;
  if (aspect > 1.75) score += (aspect - 1.75) * 1.3;
  score += (heightDiff / POSTER_WIDTH) * 1.6;
  if (columnCount === 1 && sectionCount >= 3) score += 0.4;
  if (columnCount === 3 && sectionCount <= 4) score += 0.2;
  if (sectionCount >= 6 && columnCount === 3) score -= 0.16;
  if (sectionCount >= 4 && columnCount >= 2) score -= 0.08;
  return score;
}

function fitColumnImageMaxes(sections, cuts, columnCount) {
  const preferred = imageMaxForColumns(columnCount);
  if (cuts.length <= 1) {
    return cuts.map(() => preferred);
  }

  const heights = sections.map((section) =>
    estimateSectionHeight(section, columnCount, preferred)
  );
  const imageParts = sections.map((section) =>
    estimateImageContribution(section, columnCount, preferred)
  );
  const heightsByColumn = columnHeights(heights, cuts);
  const imagesByColumn = columnHeights(imageParts, cuts);
  const shortest = Math.min(...heightsByColumn);

  return heightsByColumn.map((height, index) => {
    const overflow = height - shortest;
    const imageHeight = imagesByColumn[index];
    if (imageHeight <= 0 || overflow <= 24) return preferred;
    const reduced = Math.max(
      imageHeight - overflow,
      (imageHeight * MIN_IMAGE_MAX) / preferred
    );
    return Math.max(
      MIN_IMAGE_MAX,
      Math.min(preferred, Math.round(preferred * (reduced / imageHeight)))
    );
  });
}

function splitIntoColumns(sections) {
  const n = sections.length;
  if (n === 0) {
    return { columns: [], columnImageMaxes: [] };
  }

  const maxCount = Math.min(MAX_COLUMNS, n);
  let bestCount = 1;
  let bestScore = Infinity;
  let bestCuts = [n];

  for (let count = 1; count <= maxCount; count += 1) {
    const imageMax = imageMaxForColumns(count);
    const heights = sections.map((section) =>
      estimateSectionHeight(section, count, imageMax)
    );
    const cuts = partitionCuts(heights, count);
    const fitted = fitColumnImageMaxes(sections, cuts, count);
    const fittedHeights = sections.map((section, index) => {
      const columnIndex = cuts.findIndex((end) => index < end);
      const columnMax = fitted[columnIndex] ?? imageMax;
      return estimateSectionHeight(section, count, columnMax);
    });
    const maxHeight = maxColumnHeight(fittedHeights, cuts);
    const diff =
      Math.max(...columnHeights(fittedHeights, cuts)) -
      Math.min(...columnHeights(fittedHeights, cuts));
    const score = scoreColumnCount(count, n, maxHeight, diff);
    if (score < bestScore) {
      bestScore = score;
      bestCount = count;
      bestCuts = cuts;
    }
  }

  const columnImageMaxes = fitColumnImageMaxes(sections, bestCuts, bestCount);
  const columns = Array.from({ length: bestCount }, () => []);
  let start = 0;
  bestCuts.forEach((end, columnIndex) => {
    for (let i = start; i < end; i += 1) {
      columns[columnIndex].push({ section: sections[i], index: i });
    }
    start = end;
  });
  return { columns, columnImageMaxes };
}

function photoLayoutClass(siblingImageCount) {
  if (siblingImageCount <= 1) {
    return "share-infographic-photo share-infographic-photo--solo";
  }
  if (siblingImageCount === 2) {
    return "share-infographic-photo share-infographic-photo--pair";
  }
  return "share-infographic-photo share-infographic-photo--grid";
}

const VH_TO_PX = 8;

function convertVhValue(value) {
  if (typeof value !== "string") return value;
  return value.replace(/(\d+(?:\.\d+)?)vh/gi, (_, number) => {
    return `${Math.round(parseFloat(number) * VH_TO_PX)}px`;
  });
}

function toInfographicStyle(style, { ignoreSize } = {}) {
  if (!style || typeof style !== "object") return undefined;
  const converted = {};
  Object.entries(style).forEach(([key, value]) => {
    if (
      ignoreSize &&
      (key === "width" ||
        key === "maxWidth" ||
        key === "minWidth" ||
        key === "height" ||
        key === "maxHeight" ||
        key === "minHeight")
    ) {
      return;
    }
    converted[key] = convertVhValue(value);
  });
  return converted;
}

function ShareNode({
  item,
  convertUrl,
  ayahName,
  childIndex = 0,
  siblingImageCount = 1,
}) {
  if (!item || isMediaClass(item.className)) return null;

  const className = item.className || "";
  const isAyah = String(ayahName || "").includes("Ayah");
  const outerStyle = toInfographicStyle(item.style, {
    ignoreSize: isImageClass(className),
  });
  const innerStyle = {
    ...toInfographicStyle(item.contentStyle, {
      ignoreSize: isImageClass(className),
    }),
    ...(isAyah && childIndex === 0
      ? { fontFamily: "'Me Quran', serif", direction: "rtl" }
      : {}),
  };

  if (isImageClass(className)) {
    const raw = imageUrlFromNode(item);
    const src = raw
      ? (typeof convertUrl === "function" ? convertUrl(raw) : raw)
      : "";
    if (!src) return null;
    return (
      <div
        className={`${className} lightThemeFont ${photoLayoutClass(
          siblingImageCount
        )}`}
        style={outerStyle}
      >
        <div style={innerStyle}>
          <img src={src} alt="" crossOrigin="anonymous" />
        </div>
      </div>
    );
  }

  const allChildren = [
    ...(item.subSections || []),
    ...(item.miniSections || []),
  ];
  const siblingImages = allChildren.filter((child) =>
    isImageClass(child.className)
  ).length;

  const children = [
    ...(item.subSections || []).map((child, index) => (
      <ShareNode
        key={`sub-${index}`}
        item={child}
        convertUrl={convertUrl}
        ayahName={ayahName}
        childIndex={index}
        siblingImageCount={siblingImages}
      />
    )),
    ...(item.miniSections || []).map((child, index) => (
      <ShareNode
        key={`mini-${index}`}
        item={child}
        convertUrl={convertUrl}
        ayahName={ayahName}
        childIndex={index}
        siblingImageCount={siblingImages}
      />
    )),
  ];

  return (
    <div className={`${className} lightThemeFont`.trim()} style={outerStyle}>
      {item.content ? (
        <div style={innerStyle}>
          {ReactHtmlParser(convertVhValue(item.content))}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function randomLightColumnColors(count) {
  const startHue = Math.random() * 360;
  const step = 360 / Math.max(count, 1);
  return Array.from({ length: count }, (_, index) => {
    const hue = (startHue + index * step) % 360;
    const saturation = 38 + Math.round(Math.random() * 20);
    const lightness = 88 + Math.round(Math.random() * 6);
    return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
  });
}

const LessonShareInfographic = forwardRef(function LessonShareInfographic(
  {
    lesson,
    chapterTitle,
    lessonTitle,
    courseCardTitle,
    courseAuthor,
    convertUrl,
  },
  ref
) {
  const sections = Array.isArray(lesson?.lessonAnimation)
    ? lesson.lessonAnimation.filter(sectionHasShareableContent)
    : [];
  const { columns, columnImageMaxes } = splitIntoColumns(sections);
  const columnCount = columns.length || 1;
  const columnColors = useMemo(
    () => randomLightColumnColors(columnCount),
    [columnCount]
  );
  const labels = buildShareLabels({
    chapterTitle,
    lessonTitle: lessonTitle || lesson?.sectionTitle,
    courseCardTitle,
    courseAuthor,
  });

  return (
    <div
      ref={ref}
      className="share-infographic"
      data-columns={columnCount}
    >
      <header className="share-infographic-header">
        <div className="share-infographic-header-text">
          <h1 className="share-infographic-title">{labels.headline}</h1>
          {labels.byline ? (
            <div className="share-infographic-course">{labels.byline}</div>
          ) : null}
        </div>
        <img
          className="share-infographic-logo"
          src={hhLogo}
          alt="HH Courses"
        />
      </header>
      <div className="share-infographic-grid">
        {columns.map((column, columnIndex) => (
          <div
            className="share-infographic-column"
            key={columnIndex}
            style={{
              "--share-image-max": `${
                columnImageMaxes[columnIndex] ||
                imageMaxForColumns(columnCount)
              }px`,
              "--share-column-bg": columnColors[columnIndex],
            }}
          >
            {column.map(({ section, index }) => (
              <div
                className="share-infographic-cell"
                key={section.id || index}
              >
                <span className="share-infographic-step">{index + 1}</span>
                <ShareNode
                  item={section}
                  convertUrl={convertUrl}
                  ayahName={section.name}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <footer className="share-infographic-footer">
        {labels.footer}
      </footer>
    </div>
  );
});

export default LessonShareInfographic;
